import React from 'react';
import PropTypes from 'prop-types';
import { Wizard } from '../../../../../src/web/components';
import PopupBody from '../PopupBody';
import ThankYouBody from '../ThankYouBody';
import PopupFields from '../Form/PopupFields';
import SettingsFields from '../Form/SettingsFields';
import ThankYouFields from '../Form/ThankYouFields';
import Preview from './Preview';
import MediaGallerySelector from '../Form/PopupFields/MediaGallerySelector';

const propTypes = {
  // Popup fields
  mediumId: PropTypes.string,
  medium: PropTypes.object,
  featuredImage: PropTypes.string,
  popupImageAltText: PropTypes.string,
  setFormError: PropTypes.func.isRequired,
  popupHeaderText: PropTypes.string.isRequired,
  popupBodyContent: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  popupIncludeForm: PropTypes.bool.isRequired,
  popupButtonText: PropTypes.string.isRequired,
  setMedium: PropTypes.func.isRequired,
  // Thank you fields
  thankYouHeaderText: PropTypes.string.isRequired,
  thankYouBodyContent: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  thankYouButtonText: PropTypes.string.isRequired,
  // Settings fields
  settingsTrigger: PropTypes.string.isRequired,
  settingsTimer: PropTypes.string.isRequired,
  settingsMobile: PropTypes.bool.isRequired,
  settingsLeadSource: PropTypes.string.isRequired,
  settingsNewLeadSource: PropTypes.string.isRequired,
  errorCreatingLeadSource: PropTypes.string.isRequired,
  // Flow control and seed data
  show: PropTypes.bool.isRequired,
  saved: PropTypes.bool.isRequired,
  sources: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isSavingPopup: PropTypes.bool.isRequired,
  isCreatingLeadSource: PropTypes.bool.isRequired,
  // Flow control functions
  onSavePopup: PropTypes.func.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  onDeletePopup: PropTypes.func.isRequired,
  // Form control functions
  onControlChange: PropTypes.func.isRequired,
  onCheckboxChange: PropTypes.func.isRequired,
  onTextEditorFinish: PropTypes.func.isRequired,
  onFeaturedImageChange: PropTypes.func.isRequired,
};

const defaultProps = {
  featuredImage: undefined,
  mediumId: undefined,
  medium: undefined,
  popupImageAltText: '',
};

const POPUP = 'POPUP';
const THANK_YOU = 'THANKYOU';
const SETTINGS = 'SETTINGS';

const steps = [
  {
    name: 'Pop-up',
    value: POPUP,
  },
  {
    name: 'Thank You',
    value: THANK_YOU,
  },
  {
    name: 'Settings',
    value: SETTINGS,
  },
];

const fields = {
  [POPUP]: PopupFields,
  [THANK_YOU]: ThankYouFields,
  [SETTINGS]: SettingsFields,
};

const previews = {
  [POPUP]: PopupBody,
  [THANK_YOU]: ThankYouBody,
  [SETTINGS]: PopupBody,
};

class Builder extends React.Component {
  steps = steps; // eslint-disable-line react/sort-comp

  state = {
    currentStep: this.steps[0].value,
  };

  setCurrentStep = (step) => {
    const { setFormError, mediumId, popupImageAltText } = this.props;
    if (mediumId && !popupImageAltText) {
      setFormError('Alt text is required');
      return;
    }
    setFormError();
    this.setState({ currentStep: step });
  }

  getPropsFor = component => (
    component.attrs.reduce((obj, prop) => {
      obj[prop] = this.props[prop]; // eslint-disable-line react/destructuring-assignment, no-param-reassign, max-len
      obj.setShowGallery = val => this.setState({ showGallery: val }); // eslint-disable-line react/destructuring-assignment, no-param-reassign, max-len
      return obj;
    }, {})
  )

  render() {
    const { currentStep, showGallery } = this.state;
    const {
      medium,
      mediumId,
      show,
      saved,
      isSavingPopup,
      isCreatingLeadSource,
      onSavePopup,
      onCloseModal,
      onDeletePopup,
      setMedium,
      featuredImage,
    } = this.props;

    const Step = fields[currentStep];
    const stepProps = this.getPropsFor(Step);
    const stepComponent = (
      <Step {...stepProps} />
    );

    const StepPreview = previews[currentStep];
    const previewProps = this.getPropsFor(StepPreview);
    const previewComponent = (
      <Preview medium={medium} featuredImage={featuredImage}>
        <StepPreview {...previewProps} />
      </Preview>
    );

    return (
      <>
        <Wizard
          title="Build a Pop-up"
          show={showGallery ? false : show}
          steps={this.steps}
          currentStep={currentStep}
          stepComponent={stepComponent}
          previewComponent={previewComponent}
          isSubmitting={isSavingPopup || isCreatingLeadSource}
          onSave={onSavePopup}
          onClose={onCloseModal}
          onSelectStep={this.setCurrentStep}
          onDelete={saved ? onDeletePopup : undefined}
        />
        {showGallery && (
          <MediaGallerySelector
            existingMedium={mediumId}
            onSelect={setMedium}
            hideGallery={() => this.setState({ showGallery: false })}
          />
        )}
      </>
    );
  }
}

Builder.propTypes = propTypes;
Builder.defaultProps = defaultProps;

export default Builder;
